import React, { useState, useEffect } from "react";
import { Modal, Select, Button, Space, Card, Typography, Row, Col } from "antd";
import { VideoCameraOutlined } from "@ant-design/icons";
import ModelWrapper from "../../modal/ModelWrapper";
import { notify } from "../../../utils/helper";
import videos from "../../../services/videos";
import AddPhysicianModalWrapper from "../../modal/AddPhysicianModalWrapper";
import { CustomButton, CustomSecondaryButton } from "../physician_page/styled";

const { Option } = Select;
const { Text } = Typography;

const AssignVideoToPatientModal = ({
  isOpen,
  setOpen,
  selectedPatientIds,
  onSuccess,
  setSelectedRowKeys,
}) => {
  const [allVideos, setAllVideos] = useState([]);
  const [selectedVideoId, setSelectedVideoId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const fetchVideos = async () => {
    try {
      setLoading(true);
      const res = await videos.getVideos();
      if (res?.data?.videos?.results) {
        // console.log("res:", res?.data);
        setAllVideos(res.data.videos.results);
      }
    } catch (error) {
      notify("error", "Failed to fetch videos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchVideos();
    }
  }, [isOpen]);

  const handleCancel = () => {
    setSelectedVideoId(null);
    setSelectedRowKeys([]);
    setOpen(false);
  };

  const handleAssign = async () => {
    if (!selectedVideoId || selectedPatientIds.length === 0) return;

    try {
      setSubmitting(true);
      await videos.AssignVideoToPatient({
        patientIds: selectedPatientIds,
        videoUrl: selectedVideoId,
      });

      onSuccess();
      handleCancel();
      setSubmitting(false);
    } catch (error) {
      console.error("Error assigning video:", error);
    }
  };

  const customOptionRender = (video) => (
    <Card
      bodyStyle={{ padding: "8px" }}
      style={{ marginBottom: "8px", border: "1px solid #f0f0f0" }}
    >
      <Row gutter={16} align="middle">
        <Col flex="80px">
          {video.thumbnail ? (
            <img
              src={video.thumbnail}
              alt={video.title}
              style={{
                width: "70px",
                height: "45px",
                objectFit: "cover",
                borderRadius: "4px",
              }}
            />
          ) : (
            <div
              style={{
                width: "70px",
                height: "45px",
                background: "#f0f0f0",
                borderRadius: "4px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <VideoCameraOutlined
                style={{ fontSize: "20px", color: "#999" }}
              />
            </div>
          )}
        </Col>
        <Col flex="auto">
          <Text strong>{video.title}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: "12px" }}>
            Duration: {video.duration || "N/A"}
          </Text>
        </Col>
      </Row>
    </Card>
  );

  return (
    <AddPhysicianModalWrapper
      title={
        <div className="row-flex flex-column">
          <h3 className="font-18 font-semibold text-primary mb-5">
            Assign Video to Patient
          </h3>
          <span className="font-15 text-secondry">
            Select a video to assign to the selected patient.
          </span>
        </div>
      }
      open={isOpen}
      setShow={handleCancel}
      width={600}
      footer={[
        <Col span={24} className="d-flex justify-end gap-12">
          <CustomSecondaryButton key="cancel" onClick={handleCancel}>
            Cancel
          </CustomSecondaryButton>
          <CustomButton
            key="assign"
            type="primary"
            loading={submitting}
            disabled={!selectedVideoId}
            onClick={handleAssign}
          >
            Assign Video
          </CustomButton>
        </Col>,
      ]}
    >
      <Space direction="vertical" style={{ width: "100%" }} size="large">
        {/* <div>
          <Text>
            Assigning video to {selectedPatientIds.length} selected patient(s)
          </Text>
        </div> */}

        <div>
          <Text strong style={{ marginBottom: "8px", display: "block" }}>
            Select a video to assign:
          </Text>
          <Select
            placeholder="Select a video"
            style={{ width: "100%" }}
            value={selectedVideoId}
            onChange={setSelectedVideoId}
            loading={loading}
            optionLabelProp="label"
          >
            {allVideos.map((video) => (
              <Option key={video.id} value={video.videoUrl} label={video.title}>
                <Card
                  bodyStyle={{ padding: "8px" }}
                  style={{
                    marginBottom: "8px",
                    border: "1px solid gray",
                    borderRadius: "6px",
                  }}
                >
                  <Row gutter={16} align="middle">
                    <Col flex="80px">
                      {video.thumbnail ? (
                        <img
                          src={video.thumbnail}
                          alt={video.title}
                          style={{
                            width: "70px",
                            height: "45px",
                            objectFit: "cover",
                            borderRadius: "4px",
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            width: "70px",
                            height: "45px",
                            background: "#f0f0f0",
                            borderRadius: "4px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <VideoCameraOutlined
                            style={{ fontSize: "20px", color: "#999" }}
                          />
                        </div>
                      )}
                    </Col>
                    <Col flex="auto">
                      <Text strong className="text-primary">{video.title}</Text>
                      <br />
                      <Text type="secondary" style={{ fontSize: "12px", color: "var(--secondary-text-color)" }}>
                        Duration: {video.duration || "N/A"}
                      </Text>
                    </Col>
                  </Row>
                </Card>
              </Option>
            ))}
          </Select>
        </div>

        {selectedVideoId && (
          <Card>
            <Row gutter={16} align="middle">
              <Col span={24}>
                <Text type="secondary">Selected Video:</Text>
                <br />
                <Text strong>
                  {allVideos.find((v) => v.videoUrl === selectedVideoId)?.title}
                </Text>
              </Col>
            </Row>
          </Card>
        )}
      </Space>
    </AddPhysicianModalWrapper>
  );
};

export default AssignVideoToPatientModal;
